import * as Yup from 'yup';
import { toast } from 'react-toastify';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ButtonLoading from '@mui/lab/LoadingButton';
import { Box, FormControlLabel, Radio, RadioGroup, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useFormik } from 'formik';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { useNavigate } from 'react-router-dom';
import { PATH_CONSTANT } from 'constant/PathConstant';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { SUBJECT_CATEGORY, SUBJECT_TYPE } from 'constant/DataInputConstant';
import { ToggleButtonGroupSX } from 'utils/CommonSx';
import {
  SUBJECT_CATEGORY_DROPDOWN,
  SUBJECT_TYPE_DROPDOWN,
  SUBJECT_USER_LIST_FILTER_DROPDOWN,
} from 'constant/SubjectConstant';
import { useUpsertSubjectData } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { SubjectDataUpsertRequest } from 'types/api/SystemTenant/AROKMS/SubjectTypes';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';

const textInputStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};
const selectedMenuOptionSx = {
  backgroundColor: '#F5F5F5',
  border: '1px solid #42BB93',
  transition: 'ease-in 0.1s',
  '& svg': {
    color: '#42BB93',
  },
};

const iconSx = {
  color: '#828C99',
  fontSize: 40,
};

const menuOptionSx = {
  border: '1px solid #E0E0E0',
  p: 2,
  height: '45px',
  borderRadius: '4px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F5F5F5',
    border: '1px solid #42BB93',
    transition: 'ease-in 0.1s',
    '& svg': {
      color: '#42BB93',
    },
  },
};

export interface CreateSubjectFormikInitialValues {
  name: string | null;
  type: AutoCompleteItem | null;
  category: AutoCompleteItem | null;
  categoryFilter?: string | null;
}
export const createInsightViewDefinitionFormikInitialValues: CreateSubjectFormikInitialValues = {
  name: null,
  type: { label: 'Table', value: SUBJECT_TYPE.TABLE },
  category: { label: 'Regular', value: SUBJECT_CATEGORY.REGULAR },
  categoryFilter: 'ALL_ROLES',
};

export const createSubjectValidationSchema = Yup.object().shape({
  name: Yup.string().required('This field is required').nullable(),
  type: Yup.object().required('This field is required').nullable(),
  category: Yup.object().required('This field is required').nullable(),
  categoryFilter: Yup.string().nullable(),
});

export default function CreateSubjectDefinition() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [filter] = useState<PaginationAndSortingParams>({
    page: 0,
    size: 20,
    filterValue: '',
    sortType: 'desc',
    filterOperator: 'contains',
  });
  const { mutate: insertSubject, isLoading: isSubmitting } = useUpsertSubjectData(filter);
  const formik = useFormik({
    initialValues: createInsightViewDefinitionFormikInitialValues,
    validationSchema: createSubjectValidationSchema,
    onSubmit: () => {},
  });

  const handleOnSubmit = () => {
    if (formik.values.name && formik.values.type && formik.values.category) {
      const payload: SubjectDataUpsertRequest = {
        subject: formik.values.name,
        type: formik.values.type?.value.toString() || undefined,
        category: formik.values.category?.value.toString() || undefined,
        categoryFilter: formik.values.categoryFilter || undefined,
      };
      insertSubject(payload, {
        onSuccess: () => {
          navigate(PATH_CONSTANT.SYSTEM.KMS.DATA_BUILDER.SUBJECT.SUBJECT_LIST);
          toast.success('Subject created successfully');
        },
        onError: (error) => {
          toast.error(`Failed to create subject: ${error.message}`);
        },
      });
    }
  };

  const handleOnCancel = () => {
    navigate(PATH_CONSTANT.SYSTEM.KMS.DATA_BUILDER.SUBJECT.SUBJECT_LIST);
  };

  const isAllFormValid = createSubjectValidationSchema.isValidSync(formik.values);
  const isLoadingOptionsData = false;

  return (
    <MainLayoutComponent
      pageTitle='Create Subject Definition'
      breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'Subject Definition']}
    >
      <Box p={2} bgcolor='#fff'>
        {isLoadingOptionsData ? (
          <Stack height={450}>
            <ActivityIndicator />
          </Stack>
        ) : (
          <>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Create Subject Definition
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Stack width='100%' direction='column' gap={2} mb={2}>
              <Stack width='40%'>
                <Typography variant='input-label' fontWeight={800}>
                  Subject Name
                </Typography>
                <Typography variant='input-label-gray' fontSize={13}></Typography>
                <TextField
                  sx={textInputStyles}
                  name='name'
                  hiddenLabel
                  value={formik.values.name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  variant='outlined'
                  size='small'
                  placeholder='e.g. Asset'
                />
              </Stack>
              <Stack width='40%'>
                <Typography variant='input-label' fontWeight={800}>
                  Subject Type
                </Typography>
                <Typography variant='input-label-gray' fontSize={13}>
                  Choose the type of subject
                </Typography>

                <Stack>
                  <ToggleButtonGroup
                    color='primary'
                    value={formik.values.type?.value}
                    exclusive
                    onChange={(_, value) => {
                      const selectedOption = SUBJECT_TYPE_DROPDOWN.find((option) => option.value === value) || null;
                      formik.setFieldValue('type', selectedOption);
                    }}
                    sx={ToggleButtonGroupSX.buttonGroup}
                  >
                    {SUBJECT_TYPE_DROPDOWN.map((option) => (
                      <ToggleButton key={option.value} value={option.value} size='small'>
                        {option.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Stack>
              </Stack>
              {/* Show category only if subject type is Table */}
              {formik.values.type?.value === SUBJECT_TYPE.TABLE && (
                <Stack width='40%'>
                  <Typography variant='input-label' fontWeight={800}>
                    Subject Category
                  </Typography>
                  <Typography variant='input-label-gray' fontSize={13}>
                    Select the category for the subject
                  </Typography>
                  <Stack>
                    <Stack direction='row' gap={2} mt={2} width='100%'>
                      {SUBJECT_CATEGORY_DROPDOWN.map((option) => (
                        <Stack
                          width='33%'
                          gap={2}
                          alignItems='center'
                          direction='row'
                          onClick={() => {
                            formik.setFieldValue('category', option);
                          }}
                          sx={{
                            ...menuOptionSx,
                            ...(formik.values.category?.value === option.value ? selectedMenuOptionSx : {}),
                          }}
                        >
                          {option.value === SUBJECT_CATEGORY.REGULAR ? (
                            <TableRowsOutlinedIcon sx={iconSx} />
                          ) : (
                            <GroupOutlinedIcon sx={iconSx} />
                          )}
                          <Stack>
                            <Typography variant='body2' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                              {option.label}
                            </Typography>
                            <Typography variant='input-label-gray' fontWeight={400} component='h2' fontSize={12}>
                              {option.label === 'Regular'
                                ? 'Standard subject for storing data.'
                                : 'Subject used for storing user lists based on roles.'}
                            </Typography>
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              )}

              {/* Show category filter only if category is USER_LIST */}
              {formik.values.type?.value === SUBJECT_TYPE.TABLE &&
                formik.values.category?.value === SUBJECT_CATEGORY.USER_LIST && (
                  <Stack width='40%'>
                    <Typography variant='input-label-gray' fontSize={13}>
                      Choose the role filter to apply when synchronizing the user list.
                    </Typography>
                    <RadioGroup
                      aria-labelledby='controlled-radio-buttons-group'
                      name='controlled-radio-buttons-group'
                      value={formik.values.categoryFilter}
                      onChange={(value) => {
                        formik.setFieldValue('categoryFilter', value.target.value);
                      }}
                    >
                      {SUBJECT_USER_LIST_FILTER_DROPDOWN.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio size='small' />}
                          label={
                            <>
                              <Typography fontSize={13}>{option.label}</Typography>
                            </>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </Stack>
                )}
            </Stack>
            <Divider sx={{ my: 1 }} />

            <Stack direction='row' px={2} justifyContent='space-between' alignItems='center'>
              <Stack />
              <Stack direction='row' gap={2}>
                <ButtonLoading disabled={isSubmitting} variant='main-table-panel-border' onClick={handleOnCancel}>
                  Cancel
                </ButtonLoading>
                <ButtonLoading
                  variant='main-table-panel'
                  loading={isSubmitting}
                  onClick={handleOnSubmit}
                  disabled={!isAllFormValid}
                >
                  Save
                </ButtonLoading>
              </Stack>
            </Stack>
          </>
        )}
      </Box>
    </MainLayoutComponent>
  );
}
